import React, { useEffect, useState } from "react";

import styled from "styled-components";

import Icon from "components/atoms/Icon";
import Tooltip from "components/atoms/Tooltip";
import Alert from "components/atoms/alert/Alert";
import Confirm from "components/atoms/alert/Confirm";
import Button from "components/atoms/button/Button";
import StyledText from "components/atoms/text/StyledText";

import FormInput from "components/molecules/FormInput";
import AbstractModalFooter from "components/page/modal/AbstractModalFooter";

import {
    usePaymentPlanGroupUser,
    usePaymentPlanGroupUserCreate,
    usePaymentPlanGroupUserDelete,
} from "hooks/queries/usePayment";

import { emailValidation, genericEnterKeyDown } from "utils/validation";

const Container = styled.div`
    width: 100%;
`;

const SectionHeader = styled.div`
    display: flex;
    align-items: end;
    justify-content: space-between;
    width: 100%;
    margin-bottom: 12px;
`;

const SectionTitle = styled(StyledText).attrs({
    fontSize: "large",
    withBarType: true,
})``;

const MemberCount = styled(StyledText).attrs({
    fontSize: "small",
})``;

const AddMemberSection = styled.div`
    display: flex;
    align-items: center;
    width: 100%;
    height: 40px;
    margin-bottom: 24px;
`;

const MemberListSection = styled.div`
    display: flex;
    flex-direction: column;
    background-color: var(--color-Base1);
    padding: 8px;
    border-radius: 4px;
    margin-bottom: 32px;
`;

const MemberItem = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 8px;
    border-radius: 4px;
    &:hover {
        background-color: var(--color-Base2);
    }
`;

const MemberItemStatus = styled.div`
    display: flex;
`;

const MemberStatusBadge = styled(StyledText).attrs({
    fontSize: "small",
    color: "var(--color-SubBlack)",
})`
    background-color: var(--color-Tag);
    border-radius: 4px;
    padding: 2px 6px;
    text-align: center;
    margin-left: 8px;
`;

const MemberEmail = styled(StyledText).attrs({
    fontWeight: "bold",
    color: "var(--color-SubBlack)",
})``;

const MemberItemAction = styled.div``;

function PaymentGroupMemberModal(props) {
    const [inviteEmail, setInviteEmail] = useState("");
    const [users, setUsers] = useState([]);

    const paymentPlanGroupUserQuery = usePaymentPlanGroupUser({ group_id: props.paymentPlanGroup?.id });
    const paymentPlanGroupUserCreateMutation = usePaymentPlanGroupUserCreate();
    const paymentPlanGroupUserDeleteMutation = usePaymentPlanGroupUserDelete();

    useEffect(() => {
        if (paymentPlanGroupUserQuery.data) {
            setUsers(paymentPlanGroupUserQuery.data);
        }
    }, [paymentPlanGroupUserQuery.data]);

    const invite = () => {
        // email validation
        if (!inviteEmail) {
            return;
        }

        if (emailValidation(inviteEmail) === false) {
            Alert("warn", "에러", "이메일 형식이 올바르지 않습니다.");
            return;
        }
        const existUser = users.find((user) => user.email === inviteEmail);
        if (existUser) {
            Alert("warn", "에러", "이미 초대된 사용자입니다.");
            return;
        }

        // max_member validation
        if (users.filter((user) => user.invitation_status !== "REJECTED").length >= props.paymentPlanGroup.max_member) {
            Alert("warn", "에러", "초대할 수 있는 최대 멤버 수를 초과하였습니다.");
            return;
        }
        Confirm("info", "초대", `아래의 사용자에게 초대 이메일을 발송하겠습니까? \n - ${inviteEmail}`, "초대", () => {
            paymentPlanGroupUserCreateMutation.mutate(
                {
                    group_id: props.paymentPlanGroup.id,
                    group: props.paymentPlanGroup.id,
                    email: inviteEmail,
                },
                {
                    onSuccess: () => {
                        setInviteEmail("");
                    },
                    onError: (error) => {
                        if (error.response.data.email) {
                            Alert("warn", "에러", error.response.data.email);
                        } else {
                            Alert("warn", "에러", "서버에 오류가 발생하였습니다.");
                        }
                    },
                },
            );
        });
    };

    return (
        <Container>
            <SectionHeader>
                <SectionTitle>멤버 초대</SectionTitle>
                <MemberCount>
                    {users?.filter((user) => user.invitation_status !== "REJECTED").length}명 /{" "}
                    {props.paymentPlanGroup.max_member}명
                </MemberCount>
            </SectionHeader>
            <AddMemberSection>
                <FormInput
                    labelDp="none"
                    placeholder="초대할 이메일 주소를 입력해주세요."
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    onKeyDown={(e) => {
                        genericEnterKeyDown(e, () => {
                            if (!inviteEmail) {
                                Alert("warn", "에러", "초대할 이메일 주소를 입력해주세요.");
                                return;
                            }
                            invite();
                        });
                    }}
                />
                <Tooltip message={"초대"}>
                    <Button
                        onlyIcon
                        onClick={invite}
                        buttonSize={"40px"}
                        bgColor={"var(--color-Button1)"}
                        hoverBgColor={"var(--color-ButtonHover1)"}
                    >
                        <Icon name={"plus"} size={"12"} color={"var(--color-White)"} />
                    </Button>
                </Tooltip>
            </AddMemberSection>

            {/* 멤버 현황 */}
            <SectionHeader>
                <SectionTitle>멤버 현황</SectionTitle>
            </SectionHeader>
            <MemberListSection>
                {users.map((user) => {
                    return (
                        <MemberItem key={user.id}>
                            <MemberItemStatus>
                                <MemberEmail>{user.email}</MemberEmail>
                                <MemberStatusBadge>
                                    {user.admin === user.email && "관리자"}
                                    {user.invitation_status === "PENDING" && "수락 대기 중"}
                                    {user.invitation_status === "REJECTED" && "거절"}
                                    {user.invitation_status === "ACCEPTED" && "멤버"}
                                </MemberStatusBadge>
                            </MemberItemStatus>
                            <MemberItemAction>
                                {user.admin !== user.email && (
                                    <Tooltip message={"제외"}>
                                        <Button
                                            onlyIcon
                                            buttonSize={"24px"}
                                            bgColor={"var(--color-Button3)"}
                                            hoverBgColor={"var(--color-ButtonHover3)"}
                                            onClick={() => {
                                                Confirm(
                                                    "warn",
                                                    "알림",
                                                    `${user.email} 사용자를 멤버에서 제외하겠습니까?`,
                                                    "제외",
                                                    () => {
                                                        paymentPlanGroupUserDeleteMutation.mutate({
                                                            group_id: props.paymentPlanGroup.id,
                                                            id: user.id,
                                                        });
                                                    },
                                                );
                                            }}
                                        >
                                            <Icon name={"x"} size={"12"} color={"var(--color-White)"} />
                                        </Button>
                                    </Tooltip>
                                )}
                            </MemberItemAction>
                        </MemberItem>
                    );
                })}
            </MemberListSection>
            <AbstractModalFooter onlyLeftBtn leftBtnText={"닫기"} leftOnClick={props.exitModal} />
        </Container>
    );
}

export default PaymentGroupMemberModal;
